import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import BlockChart from './BlockChart'
import DataChart from './DataChart'
import AttributeChart, { compoundKeyFor } from './AttributeChart'

import Legend from './Legend'

import { stateSelector, shouldUpdate } from './selectors'

import './SplitChart.scss'

const CLASS = 'insights-portal-split-chart'

const SplitChart = props => {
  const subjectType = props.subjectType || ''

  const { subjects, splitOptions, currentSplit, groupFrom, groupTo } = usePool(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const splitIds = useMemo(
    () =>
      currentSplit
        ? props.items.reduce((acc, cur) => {
            const waveIndex = subjects?.[cur.uid]?.wave?.index
            return acc.includes(waveIndex) ? acc : acc.concat(waveIndex)
          }, [])
        : [],
    [currentSplit, props.items, subjects]
  )

  const attributeMode = 'concept' === subjectType && !currentSplit

  const scrollLeft = attributeMode ? 0 : props.scrollLeft

  const className = props.className || CLASS

  const chartWidth = attributeMode ? props.blockWidth - props.paddingLeft : props.items.length * props.columnWidth

  const containerStyle = useMemo(
    () =>
      props.items.length
        ? {
            width: `${chartWidth}px`,
            flex: `0 0 ${chartWidth}px`,
            top: 0,
            left: `${props.paddingLeft - scrollLeft}px`
          }
        : null,
    [props.items.length, chartWidth, props.paddingLeft, scrollLeft]
  )

  const chartStyle = useMemo(
    () =>
      splitOptions?.length ? { width: `${splitOptions.length * props.columnWidth}px` } : { width: `${chartWidth}px` },
    [splitOptions, props.columnWidth, chartWidth]
  )

  const charts = useMemo(
    () =>
      props.wait
        ? []
        : currentSplit
        ? splitIds.map((v, i) =>
            i >= groupFrom && i <= groupTo ? (
              <BlockChart
                key={`${v}-${i}`}
                wait={props.wait}
                style={chartStyle}
                waveIndex={v}
                items={props.items}
                subjectType={subjectType}
                onChartTypeChange={props.onChartTypeChange}
              />
            ) : (
              <BlockChart.Empty key={`${v}-${i}`} style={chartStyle}></BlockChart.Empty>
            )
          )
        : [
            'concept' === subjectType ? (
              <AttributeChart key={compoundKeyFor(props.items)} wait={props.wait} items={props.items} />
            ) : (
              <DataChart
                key={0}
                wait={props.wait}
                style={chartStyle}
                items={props.items}
                scrollLeft={scrollLeft}
                paddingLeft={props.paddingLeft}
                columnWidth={props.columnWidth}
                blockWidth={props.blockWidth}
                onChartTypeChange={props.onChartTypeChange}
              />
            )
          ],
    [
      props.wait,
      props.items,
      props.paddingLeft,
      props.columnWidth,
      props.blockWidth,
      props.onChartTypeChange,
      currentSplit,
      splitIds,
      subjectType,
      chartStyle,
      scrollLeft,
      groupFrom,
      groupTo
    ]
  )

  const wrapperStyle = useMemo(
    () =>
      Object({
        width: `${props.blockWidth}px`,
        flex: `0 0 ${props.blockWidth}px`
      }),
    [props.blockWidth]
  )

  return (
    <div className={className} style={wrapperStyle}>
      <Legend
        key={-1}
        wait={props.wait}
        width={props.paddingLeft}
        style={{
          paddingRight: getCSSValue(props.paddingSpacerLeft)
        }}
        attributeMode={attributeMode}
        onLegendClick={props.onLegendClick}
      />
      <div className={`${className}-container`} style={containerStyle}>
        {charts}
      </div>
    </div>
  )
}

SplitChart.propTypes = {
  className: PropTypes.string,
  wait: PropTypes.any,
  items: PropTypes.arrayOf(PropTypes.object),
  timeout: PropTypes.number,
  scrollLeft: PropTypes.number,
  paddingLeft: PropTypes.number.isRequired,
  paddingSpacerLeft: PropTypes.number.isRequired,
  columnWidth: PropTypes.number.isRequired,
  blockWidth: PropTypes.number,
  subjectType: PropTypes.string,
  onLegendClick: PropTypes.func,
  onChartTypeChange: PropTypes.func
}

SplitChart.defaultProps = {
  items: [],
  timeout: 700,
  scrollLeft: 0
}

export default SplitChart

function getCSSValue(input) {
  return 'number' === typeof input ? `${input}px` : 'string' === typeof input ? input : ''
}
